import { useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  StationApproveInput,
  StationProbe,
  StationSubmissionRow,
  StationSubmissionStatus,
} from "@gwrctl/api-client";
import { client } from "../lib/client.js";
import { humanizeError } from "../lib/errors.ts";
import { useToast } from "../lib/toast.tsx";
import { PageBody, PageHeader } from "../components/Shell.tsx";
import { Banner, Button, Card, Empty, ErrorState, Field, Modal } from "../components/Ui.tsx";

/**
 * Raadiocast "Radyo öner" / "Radyonu ekle" başvuruları.
 *
 * Her başvuru sunucuda bir kez yayın kontrolünden geçer (ICY başlıkları, ilk
 * bayt). Onaylanan TR istasyonu Firestore `settings/radio_list`'e eklenir
 * (uygulama listeyi 24 saat önbelleklediği için en geç bir gün içinde görünür);
 * diğer ülkeler radio-browser'a gönderilir. Aynı adres birden çok kez önerildiyse
 * sayaç artar, yeni satır açılmaz.
 */
const APP_ID = "raadiocast";

const STATUS_LABEL: Record<StationSubmissionStatus, string> = {
  pending: "Bekleyen",
  approved: "Onaylanan",
  rejected: "Reddedilen",
  all: "Tümü",
};

const SOURCE_LABEL: Record<StationSubmissionRow["source"], string> = {
  owner: "Yayıncı",
  user: "Öneri",
  crawler: "Tarayıcı",
};

export function StationSubmissionsPage(): ReactNode {
  const [status, setStatus] = useState<StationSubmissionStatus>("pending");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState<StationSubmissionRow | null>(null);

  const list = useQuery({
    queryKey: ["station-submissions", status],
    queryFn: () => client.stationSubmissions(APP_ID, status),
  });

  const needle = search.trim().toLowerCase();
  const rows = (list.data?.submissions ?? []).filter(
    (row) =>
      !needle ||
      row.name.toLowerCase().includes(needle) ||
      (row.streamUrl ?? "").toLowerCase().includes(needle) ||
      (row.website ?? "").toLowerCase().includes(needle),
  );

  return (
    <>
      <PageHeader
        kicker="raadiocast"
        title="İstasyon başvuruları"
        actions={
          <select
            className="input !w-auto"
            aria-label="Durum"
            value={status}
            onChange={(e) => setStatus(e.target.value as StationSubmissionStatus)}
          >
            {(Object.keys(STATUS_LABEL) as StationSubmissionStatus[]).map((key) => (
              <option key={key} value={key}>
                {STATUS_LABEL[key]}
              </option>
            ))}
          </select>
        }
      />

      <PageBody>
        {list.data && !list.data.firestore.configured && (
          <Banner tone="warn" kicker="firestore">
            Sunucuda RAADIOCAST_FIREBASE_SA_PATH tanımlı değil. TR başvuruları onaylanamaz ve
            listede zaten olanlar işaretlenemez; diğer ülkeler radio-browser'a gönderilebilir.
          </Banner>
        )}

        <Card
          title={list.data ? `${rows.length} başvuru` : "Başvurular"}
          hint={
            list.data?.firestore.count != null
              ? `TR listesinde ${list.data.firestore.count} istasyon`
              : "sunucu her adresi bir kez kontrol eder"
          }
          action={
            <input
              className="input w-full sm:!w-56"
              aria-label="Ad ya da adres ara"
              placeholder="ad ya da adres ara"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          }
          flush
        >
          {list.isError ? (
            <ErrorState error={list.error} onRetry={() => void list.refetch()} />
          ) : list.isLoading ? (
            <Empty>Yükleniyor…</Empty>
          ) : rows.length ? (
            rows.map((row) => <SubmissionRow key={row.id} row={row} onOpen={() => setOpen(row)} />)
          ) : needle ? (
            <Empty>“{search.trim()}” ile eşleşen başvuru yok</Empty>
          ) : (
            <Empty>{status === "pending" ? "Bekleyen başvuru yok" : "Başvuru yok"}</Empty>
          )}
        </Card>
      </PageBody>

      {open && <ReviewModal row={open} onClose={() => setOpen(null)} />}
    </>
  );
}

function ProbeTag({ check }: { check: StationProbe | null }): ReactNode {
  if (!check) return <span className="tag tag-outline">kontrol bekliyor</span>;
  if (check.ok) {
    return (
      <span className="tag tag-accent" title={check.contentType}>
        {check.kind === "hls" ? "HLS" : "yayın"} çalışıyor
        {check.bitrate ? ` · ${check.bitrate} kbps` : ""}
      </span>
    );
  }
  return (
    <span className="tag tag-outline" style={{ color: "var(--color-danger)", borderColor: "var(--color-danger)" }}>
      {check.error ?? "açılmıyor"}
    </span>
  );
}

function SubmissionRow({ row, onOpen }: { row: StationSubmissionRow; onOpen: () => void }): ReactNode {
  return (
    <div className="row flex-wrap items-center gap-3">
      {row.logoUrl ? (
        <img
          src={row.logoUrl}
          alt=""
          className="h-10 w-10 shrink-0 rounded-[var(--radius-sm)] object-contain"
          style={{ background: "var(--color-neutral-900)" }}
          referrerPolicy="no-referrer"
        />
      ) : (
        <span className="h-10 w-10 shrink-0 rounded-[var(--radius-sm)]" style={{ background: "var(--color-neutral-900)" }} />
      )}
      <div className="grid min-w-0 flex-1 basis-[240px] gap-1">
        <span className="flex flex-wrap items-center gap-2 text-neutral-100">
          {row.name}
          <span className={`tag ${row.source === "owner" ? "tag-accent" : "tag-neutral"}`}>
            {SOURCE_LABEL[row.source]}
          </span>
          {row.submitCount > 1 && <span className="tag tag-outline">×{row.submitCount}</span>}
          {row.country && <span className="tag tag-neutral">{row.country}</span>}
          {row.city && <span className="tag tag-outline">{row.city}</span>}
          {row.genre && <span className="tag tag-outline">{row.genre}</span>}
          {row.streamUrl && <ProbeTag check={row.check} />}
          {row.existsInList && (
            <span className="tag tag-outline" style={{ color: "var(--color-warn)", borderColor: "var(--color-warn)" }}>
              listede var: {row.existsInList}
            </span>
          )}
          {row.status !== "pending" && <span className="tag tag-neutral">{STATUS_LABEL[row.status]}</span>}
        </span>
        {row.streamUrl ? (
          <a href={row.streamUrl} target="_blank" rel="noreferrer" className="mono truncate text-xs text-accent-300 hover:underline">
            {row.streamUrl}
          </a>
        ) : (
          <span className="text-xs text-neutral-500">yayın adresi verilmedi</span>
        )}
        {row.message && <span className="line-clamp-2 text-xs text-neutral-400">“{row.message}”</span>}
        <span className="text-2xs text-neutral-600">
          {new Date(row.lastSubmittedAt).toLocaleString("tr-TR")}
          {row.contactEmail ? ` · ${row.contactEmail}` : ""}
          {row.website ? ` · ${row.website}` : ""}
          {row.appVersion ? ` · v${row.appVersion}` : ""}
        </span>
      </div>
      <Button variant="ghost" className="!ml-auto" onClick={onOpen}>
        {row.status === "pending" ? "İncele" : "Aç"}
      </Button>
    </div>
  );
}

function ReviewModal({ row, onClose }: { row: StationSubmissionRow; onClose: () => void }): ReactNode {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState<StationApproveInput>({
    name: row.name,
    streamUrl: row.check?.resolvedUrl ?? row.streamUrl ?? "",
    logoUrl: row.logoUrl ?? "",
    website: row.website ?? row.check?.icyUrl ?? "",
    tags: row.genre ?? row.check?.icyGenre ?? "",
    city: row.city ?? "",
    country: (row.country ?? "TR").toUpperCase(),
    votes: 0,
    note: "",
  });
  const [check, setCheck] = useState<StationProbe | null>(row.check);
  const [rejectNote, setRejectNote] = useState("");
  const set = <K extends keyof StationApproveInput>(key: K, value: StationApproveInput[K]): void =>
    setForm((f) => ({ ...f, [key]: value }));

  const done = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["station-submissions"] });
    onClose();
  };

  const recheck = useMutation({
    mutationFn: () => client.recheckStationSubmission(row.id, form.streamUrl || undefined),
    onSuccess: (res) => setCheck(res.check),
    onError: (err) => toast({ tone: "danger", title: "Kontrol edilemedi", body: humanizeError(err) }),
  });

  const approve = useMutation({
    mutationFn: () =>
      client.approveStationSubmission(row.id, {
        ...form,
        logoUrl: form.logoUrl || undefined,
        website: form.website || undefined,
        tags: form.tags || undefined,
        city: form.city || undefined,
        note: form.note || undefined,
      }),
    onSuccess: (res) => {
      toast({
        tone: "ok",
        title: res.published.target === "firestore" ? "TR listesine eklendi" : "radio-browser'a gönderildi",
      });
      done();
    },
    onError: (err) => toast({ tone: "danger", title: "Onaylanamadı", body: humanizeError(err) }),
  });

  const reject = useMutation({
    mutationFn: () => client.rejectStationSubmission(row.id, rejectNote || undefined),
    onSuccess: () => {
      toast({ tone: "ok", title: "Reddedildi" });
      done();
    },
    onError: (err) => toast({ tone: "danger", title: "Reddedilemedi", body: humanizeError(err) }),
  });

  const isTr = form.country.toUpperCase() === "TR";
  const busy = approve.isPending || reject.isPending;
  const editable = row.status === "pending";

  return (
    <Modal
      title={row.name}
      subtitle={`${SOURCE_LABEL[row.source]} · ${row.submitCount} kez gönderildi`}
      onClose={onClose}
      dismissable={!busy}
      width={620}
      footer={
        editable ? (
          <>
            <Button variant="ghost" onClick={() => reject.mutate()} loading={reject.isPending} disabled={busy}>
              Reddet
            </Button>
            <Button
              variant="primary"
              onClick={() => approve.mutate()}
              loading={approve.isPending}
              disabled={busy || !form.streamUrl || form.name.trim().length < 2 || form.country.length !== 2}
            >
              {isTr ? "Onayla · TR listesine ekle" : "Onayla · radio-browser'a gönder"}
            </Button>
          </>
        ) : undefined
      }
    >
      <div className="grid gap-3">
        {row.existsInList && (
          <Banner tone="warn">TR listesinde benzer kayıt var: {row.existsInList}</Banner>
        )}
        {row.message && <p className="text-sm text-neutral-300">“{row.message}”</p>}
        {row.contactEmail && (
          <p className="text-xs text-neutral-500">
            İletişim: <a className="text-accent-300 hover:underline" href={`mailto:${row.contactEmail}`}>{row.contactEmail}</a>
          </p>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Ad">
            <input className="input" value={form.name} disabled={!editable} onChange={(e) => set("name", e.target.value)} />
          </Field>
          <Field label="Ülke" hint="TR → Firestore, diğerleri → radio-browser">
            <input
              className="input"
              maxLength={2}
              value={form.country}
              disabled={!editable}
              onChange={(e) => set("country", e.target.value.toUpperCase())}
            />
          </Field>
        </div>

        <Field label="Yayın adresi" hint={check ? <ProbeTag check={check} /> : undefined}>
          <div className="flex gap-2">
            <input
              className="input mono flex-1"
              value={form.streamUrl}
              disabled={!editable}
              onChange={(e) => set("streamUrl", e.target.value)}
            />
            <Button onClick={() => recheck.mutate()} loading={recheck.isPending} disabled={!form.streamUrl}>
              Kontrol et
            </Button>
          </div>
        </Field>
        {check?.ok && (check.icyName || check.icyGenre) && (
          <p className="text-2xs text-neutral-500">
            Yayının kendi bilgisi: {check.icyName ?? "?"}
            {check.icyGenre ? ` · ${check.icyGenre}` : ""}
            {check.icyUrl ? ` · ${check.icyUrl}` : ""}
          </p>
        )}

        <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
          <Field label="Logo adresi">
            <input className="input mono" value={form.logoUrl} disabled={!editable} onChange={(e) => set("logoUrl", e.target.value)} />
          </Field>
          {form.logoUrl ? (
            <img
              src={form.logoUrl}
              alt="logo önizleme"
              className="h-16 w-16 rounded-[var(--radius-sm)] object-contain"
              style={{ background: "var(--color-neutral-900)" }}
              referrerPolicy="no-referrer"
            />
          ) : null}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Web sitesi">
            <input className="input mono" value={form.website} disabled={!editable} onChange={(e) => set("website", e.target.value)} />
          </Field>
          <Field label="Türler" hint="virgülle: Pop, Haber">
            <input className="input" value={form.tags} disabled={!editable} onChange={(e) => set("tags", e.target.value)} />
          </Field>
          <Field label="Şehir">
            <input className="input" value={form.city} disabled={!editable} onChange={(e) => set("city", e.target.value)} />
          </Field>
          {isTr && (
            <Field label="Sıralama puanı (votes)" hint="9000 üstü popüler listeye girer">
              <input
                className="input"
                type="number"
                min={0}
                value={form.votes}
                disabled={!editable}
                onChange={(e) => set("votes", Number(e.target.value) || 0)}
              />
            </Field>
          )}
        </div>

        {editable && (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Onay notu (isteğe bağlı)">
              <input className="input" value={form.note} onChange={(e) => set("note", e.target.value)} />
            </Field>
            <Field label="Red nedeni (isteğe bağlı)">
              <input className="input" value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} />
            </Field>
          </div>
        )}

        {!editable && (
          <p className="text-xs text-neutral-500">
            {STATUS_LABEL[row.status]}
            {row.reviewedAt ? ` · ${new Date(row.reviewedAt).toLocaleString("tr-TR")}` : ""}
            {row.published ? ` · ${row.published.target} ${row.published.stationId}` : ""}
            {row.reviewNote ? ` · ${row.reviewNote}` : ""}
          </p>
        )}
      </div>
    </Modal>
  );
}
